import { useState, useMemo } from 'react';
import { type UseFormSetError } from 'react-hook-form';
import {
  usePickupDelegations,
  useCreatePickupDelegation,
  useUpdatePickupDelegation,
  useDeletePickupDelegation,
  usePickupDelegationById,
} from '../../../hooks/queries/useVisitorManagement';
import { useSites } from '../../../hooks/queries/useSites';
import type { PlanningStudent } from '../../../types/ReunificationTypes';
import type {
  PickupDelegation,
  PickupDelegationWrite,
} from '../../../types/VisitorManagementTypes';
import { toaster } from '../../ui/toaster';
import { ApiValidationError } from '../../../lib/http-client';
import { StatusBadge, type Column, type StatusType } from '@themes/aegix';
import { useSearchParams } from 'react-router-dom';
import { formatLocalDateTime } from '../../../lib/date';

interface DelegationRow {
  [key: string]: unknown;
  id: string;
  student_name: string;
  delegate_name: string;
  delegate_phone: string;
  relationship: string;
  valid_from: string;
  valid_until: string;
  is_active: boolean;
}

export function usePickupDelegationCrud({ students }: { students?: PlanningStudent[] } = {}) {
  const [searchParams] = useSearchParams();
  const urlId = searchParams.get('public_id');
  const urlTab = searchParams.get('subtab');
  const isDelegationTab =
    urlTab?.toLowerCase().replace(/\s+/g, '-') === 'pickup-delegation';
  const { data: delegationById, error: delegationByIdError } = usePickupDelegationById(
    urlId as string,
    isDelegationTab,
  );
  const { data, isLoading, error } = usePickupDelegations();
  const { data: sitesData } = useSites({ page_size: 100 });
  const createMutation = useCreatePickupDelegation();
  const updateMutation = useUpdatePickupDelegation();
  const deleteMutation = useDeletePickupDelegation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editItem, setEditItem] = useState<PickupDelegation | null>(null);

  const isSingleView = Boolean(
    urlId && isDelegationTab && delegationById && !delegationByIdError,
  );
  const delegationsDetails =
    isSingleView && delegationById ? [delegationById] : (data ?? []);

  const rows: DelegationRow[] = delegationsDetails
    .filter((r): r is PickupDelegation => !!r)
    .map((r) => ({
      id: r.public_id,
      student_name: r.student_name || `Student #${r.student}`,
      delegate_name: r.delegate_name,
      delegate_phone: r.delegate_phone || '',
      relationship: r.relationship || '',
      valid_from: r.valid_from ? new Date(r.valid_from).toLocaleString() : '',
      valid_until: r.valid_until ? new Date(r.valid_until).toLocaleString() : 'Indefinite',
      is_active: r.is_active,
    }));

  const columns: Column<DelegationRow>[] = [
    { key: 'student_name', label: 'Student', sortable: true, width: '180px' },
    { key: 'delegate_name', label: 'Delegate', sortable: true, width: '150px' },
    { key: 'delegate_phone', label: 'Phone', width: '120px' },
    { key: 'relationship', label: 'Relationship', width: '120px' },
    { key: 'valid_from', label: 'Valid From', width: '160px' },
    { key: 'valid_until', label: 'Valid Until', width: '160px' },
    {
      key: 'is_active',
      label: 'Active',
      width: '100px',
      render: (row) => (
        <StatusBadge
          status={
            row.is_active ? ('active' as StatusType) : ('inactive' as StatusType)
          }
          label={row.is_active ? 'Active' : 'Inactive'}
        />
      ),
    },
  ];

  const handleClose = () => {
    setDrawerOpen(false);
    setEditItem(null);
  };

  const handleRowClick = (row: DelegationRow) => {
    const item = data?.find((r) => r.public_id === row.id);
    if (item) {
      setEditItem(item);
      setDrawerOpen(true);
    }
  };

  const handleSubmit = (
    values: PickupDelegationWrite,
    setError: UseFormSetError<PickupDelegationWrite>,
  ) => {
    const onError = (err: Error) => {
      if (err instanceof ApiValidationError) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          setError(field as keyof PickupDelegationWrite, {
            message: messages.join(', '),
          });
        }
      }
      toaster.create({
        title: 'Save failed',
        description: err.message,
        type: 'error',
      });
    };

    // Clean up empty valid_until and format dates, and strip school from the backend request payload
    const { school, ...restValues } = values;
    const submitValues = {
      ...restValues,
      valid_from: values.valid_from ? new Date(values.valid_from).toISOString() : new Date().toISOString(),
      valid_until: values.valid_until ? new Date(values.valid_until).toISOString() : null,
    };

    if (editItem) {
      updateMutation.mutate(
        { publicId: editItem.public_id, data: submitValues },
        {
          onSuccess: () => {
            toaster.create({ title: 'Pickup delegation updated', type: 'success' });
            handleClose();
          },
          onError,
        },
      );
    } else {
      createMutation.mutate(submitValues, {
        onSuccess: () => {
          toaster.create({ title: 'Pickup delegation created', type: 'success' });
          handleClose();
        },
        onError,
      });
    }
  };

  const handleDelete = () => {
    if (!editItem) return;
    deleteMutation.mutate(editItem.public_id, {
      onSuccess: () => {
        toaster.create({ title: 'Pickup delegation deleted', type: 'success' });
        handleClose();
      },
      onError: (err) =>
        toaster.create({
          title: 'Delete failed',
          description: err.message,
          type: 'error',
        }),
    });
  };

  const initialValues: PickupDelegationWrite = useMemo(() => {
    const studentObj = editItem ? students?.find((s) => s.id === editItem.student) : undefined;
    const initialSchool = studentObj
      ? sitesData?.results?.find((site) => site.name === studentObj.school_name)?.id || studentObj.school || 0
      : 0;

    return editItem
      ? {
        student: editItem.student,
        school: initialSchool,
        delegate_name: editItem.delegate_name,
        delegate_phone: editItem.delegate_phone || '',
        delegate_email: editItem.delegate_email || '',
        relationship: editItem.relationship || '',
        valid_from: editItem.valid_from ? formatLocalDateTime(editItem.valid_from) : formatLocalDateTime(new Date()),
        valid_until: editItem.valid_until ? formatLocalDateTime(editItem.valid_until) : '',
        is_active: editItem.is_active,
        notes: editItem.notes || '',
      }
      : {
        student: 0,
        school: 0,
        delegate_name: '',
        delegate_phone: '',
        delegate_email: '',
        relationship: '',
        valid_from: formatLocalDateTime(new Date()),
        valid_until: '',
        is_active: true,
        notes: '',
      };
  }, [editItem, students, sitesData]);

  return {
    data: delegationsDetails,
    isLoading,
    error,
    drawerOpen,
    setDrawerOpen,
    editItem,
    handleClose,
    handleSubmit,
    handleDelete,
    handleRowClick,
    initialValues,
    rows,
    columns,
    sitesData,
  };
}
